import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton, InputAdornment,
  InputLabel, MenuItem, Paper, Select, Snackbar, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MapIcon from '@mui/icons-material/Map';
import StraightenIcon from '@mui/icons-material/Straighten';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Swal from 'sweetalert2';
import api from '../adapters/api';

const trimCode = (v) => (v == null ? v : String(v).trim());
const formatNumber = (n) => Number(n || 0).toLocaleString('th-TH');
const areaKey = (a) => `${a.province}|${a.amper}|${a.tambon || '*'}`;
const priceField = (i) => `price_${i}`;
const toPrice = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

const showError = (err) => Swal.fire({
  icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.error || err.message || '',
  confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง',
});

// รวมแถว (พื้นที่ × ประเภทรถ) ให้เป็น 1 แถวต่อพื้นที่ มีราคาของแต่ละรถเป็นคอลัมน์
function pivotAreas(rows, vehicles) {
  const byArea = new Map();
  for (const r of rows) {
    const key = areaKey(r);
    if (!byArea.has(key)) {
      byArea.set(key, {
        id: key, province: r.province, amper: r.amper, tambon: r.tambon || null,
        province_name: r.province_name || r.province, amper_name: r.amper_name || r.amper, tambon_name: r.tambon_name,
        duplicates: [],
      });
    }
    const area = byArea.get(key);
    const i = vehicles.findIndex(v => v.code === r.transport_type);
    if (i < 0) continue;
    if (area[priceField(i)] !== undefined) area.duplicates.push(r.transport_type);
    else area[priceField(i)] = r.shipping_cost;
  }
  for (const area of byArea.values()) {
    vehicles.forEach((_, i) => { if (area[priceField(i)] === undefined) area[priceField(i)] = null; });
  }
  return [...byArea.values()];
}

// ── ลำดับการคิด ───────────────────────────────────────────────────────────────

function CalculationOrder() {
  const steps = [
    { icon: <ReceiptLongIcon fontSize="small" />, title: 'บิลเก่า', text: 'ค่าส่งจากบิลล่าสุดที่ส่งที่อยู่เดียวกัน' },
    { icon: <MapIcon fontSize="small" />, title: 'เรทเหมา', text: 'ตามตำบล ถ้าไม่มีใช้ของทั้งอำเภอ' },
    { icon: <StraightenIcon fontSize="small" />, title: 'ตามระยะทาง', text: 'อัตรา/km, ขั้นต่ำ, ส่งฟรี' },
  ];
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, flexWrap: 'wrap' }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.title}>
          {i > 0 && <ChevronRightIcon sx={{ alignSelf: 'center', color: 'text.disabled' }} />}
          <Paper variant="outlined" sx={{ px: 1.5, py: 1, display: 'flex', gap: 1, alignItems: 'center', minWidth: 200, flex: '1 1 200px' }}>
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {s.icon}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>{i + 1}. {s.title}</Typography>
              <Typography variant="caption" color="text.secondary">{s.text}</Typography>
            </Box>
          </Paper>
        </React.Fragment>
      ))}
    </Box>
  );
}

// ── Dialog เพิ่ม/แก้พื้นที่ ─────────────────────────────────────────────────────────

const WHOLE_AMPER = { code: null, name_1: 'ทั้งอำเภอ (ทุกตำบลที่ไม่มีเรทเฉพาะ)' };

function AreaDialog({ open, onClose, onSaved, initial, vehicles, provinces, ampers, tambons, existingKeys }) {
  const emptyPrices = () => Object.fromEntries(vehicles.map(v => [v.code, '']));
  const [area, setArea] = useState({ province: null, amper: null, tambon: null });
  const [prices, setPrices] = useState(emptyPrices);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setArea({ province: initial.province, amper: initial.amper, tambon: initial.tambon });
      setPrices(Object.fromEntries(vehicles.map((v, i) => [v.code, initial[priceField(i)] ?? ''])));
    } else {
      setArea({ province: null, amper: null, tambon: null });
      setPrices(emptyPrices());
    }
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const amperOptions = useMemo(() => ampers.filter(a => a.province === area.province), [ampers, area.province]);
  const tambonOptions = useMemo(() => [WHOLE_AMPER, ...tambons.filter(t => t.amper === area.amper)], [tambons, area.amper]);

  const takenByOther = area.province && area.amper
    && existingKeys.has(areaKey(area))
    && (!initial || areaKey(initial) !== areaKey(area));
  const hasPrice = Object.values(prices).some(p => toPrice(p) !== null);
  const badPrice = Object.values(prices).some(p => p !== '' && (toPrice(p) === null || toPrice(p) < 0));
  const canSave = area.province && area.amper && hasPrice && !badPrice && !takenByOther;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/api/shipping-settings/areas/save', {
        area,
        original: initial ? { province: initial.province, amper: initial.amper, tambon: initial.tambon } : undefined,
        prices: Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, toPrice(v)])),
      }, { withCredentials: true });
      Swal.fire({ icon: 'success', title: 'บันทึกสำเร็จ', timer: 1200, showConfirmButton: false });
      onSaved(); onClose();
    } catch (err) {
      showError(err);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'แก้ไขเรทเหมา' : 'เพิ่มพื้นที่เรทเหมา'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">1. เลือกพื้นที่</Typography>
          <Autocomplete
            size="small"
            options={provinces}
            getOptionLabel={o => o.name_1}
            value={provinces.find(p => p.code === area.province) || null}
            onChange={(_, v) => setArea({ province: v?.code || null, amper: null, tambon: null })}
            renderInput={params => <TextField {...params} label="จังหวัด" required autoFocus={!initial} />}
          />
          <Autocomplete
            size="small"
            options={amperOptions}
            disabled={!area.province}
            getOptionLabel={o => o.name_1}
            value={amperOptions.find(a => a.code === area.amper) || null}
            onChange={(_, v) => setArea(a => ({ ...a, amper: v?.code || null, tambon: null }))}
            renderInput={params => <TextField {...params} label="อำเภอ" required />}
          />
          <Autocomplete
            size="small"
            options={tambonOptions}
            disabled={!area.amper}
            disableClearable
            getOptionLabel={o => o.name_1}
            isOptionEqualToValue={(o, v) => o.code === v.code}
            value={tambonOptions.find(t => t.code === area.tambon) || WHOLE_AMPER}
            onChange={(_, v) => setArea(a => ({ ...a, tambon: v?.code || null }))}
            renderInput={params => <TextField {...params} label="ตำบล" />}
          />
          {takenByOther && (
            <Alert severity="warning" sx={{ py: 0 }}>พื้นที่นี้มีเรทอยู่แล้ว — แก้ไขจากแถวเดิมในตาราง</Alert>
          )}

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">2. ค่าส่งแต่ละประเภทรถ (เว้นว่าง = ไม่มีเรทเหมาสำหรับรถนั้น)</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {vehicles.map(v => (
              <TextField
                key={v.code}
                size="small" type="number" fullWidth
                label={v.code}
                value={prices[v.code] ?? ''}
                onChange={e => setPrices(p => ({ ...p, [v.code]: e.target.value }))}
                error={prices[v.code] !== '' && toPrice(prices[v.code]) !== null && toPrice(prices[v.code]) < 0}
                inputProps={{ min: 0, step: 50 }}
                InputProps={{ endAdornment: <InputAdornment position="end">บาท</InputAdornment> }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#757575' }}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}>{saving ? <CircularProgress size={18} /> : 'บันทึก'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── แท็บเรทเหมา ─────────────────────────────────────────────────────────────────

function AreaRatesTab({ vehicles }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [provinceFilter, setProvinceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTarget, setDialogTarget] = useState(null);
  const [toast, setToast] = useState('');
  const [provinces, setProvinces] = useState([]);
  const [ampers, setAmpers] = useState([]);
  const [tambons, setTambons] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/api/shipping-settings/areas/list', {}, { withCredentials: true });
      setRows(data || []);
    } catch (err) { showError(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const norm = list => (list || []).map(r => ({ ...r, code: trimCode(r.code), amper: trimCode(r.amper), province: trimCode(r.province), name_1: trimCode(r.name_1) }));
    Promise.all([
      api.post('/api/getProvinceList', {}, { withCredentials: true }),
      api.post('/api/getAmperList', {}, { withCredentials: true }),
      api.post('/api/getTambonList', {}, { withCredentials: true }),
    ]).then(([p, a, t]) => {
      setProvinces(norm(p.data));
      setAmpers(norm(a.data));
      setTambons(norm(t.data));
    }).catch(showError);
  }, [load]);

  const areas = useMemo(() => pivotAreas(rows, vehicles), [rows, vehicles]);
  const existingKeys = useMemo(() => new Set(areas.map(a => a.id)), [areas]);
  const provinceOptions = useMemo(
    () => [...new Map(areas.map(a => [a.province, a.province_name])).entries()],
    [areas]
  );

  const filtered = useMemo(() => {
    const q = search.trim();
    return areas.filter(a =>
      (!provinceFilter || a.province === provinceFilter) &&
      (!q || [a.amper_name, a.tambon_name].some(s => s && s.includes(q)))
    );
  }, [areas, provinceFilter, search]);

  const saveRowPrices = async (newRow, oldRow) => {
    const prices = Object.fromEntries(vehicles.map((v, i) => [v.code, toPrice(newRow[priceField(i)])]));
    const unchanged = vehicles.every((_, i) => toPrice(oldRow[priceField(i)]) === prices[vehicles[i].code]);
    if (unchanged) return oldRow;
    if (Object.values(prices).some(p => p !== null && p < 0)) throw new Error('ค่าส่งต้องไม่ติดลบ');
    if (Object.values(prices).every(p => p === null)) {
      throw new Error('ต้องมีค่าส่งอย่างน้อย 1 ประเภทรถ — ถ้าไม่ใช้พื้นที่นี้แล้ว ให้กดปุ่มลบ');
    }
    const area = { province: newRow.province, amper: newRow.amper, tambon: newRow.tambon };
    await api.post('/api/shipping-settings/areas/save', { area, original: area, prices }, { withCredentials: true });
    setToast('บันทึกแล้ว');
    load(); // รีโหลดเพื่อให้ตารางตรงกับ DB (เช่น แถวซ้ำที่ถูกรวม)
    return { ...newRow, ...Object.fromEntries(vehicles.map((v, i) => [priceField(i), prices[v.code]])), duplicates: [] };
  };

  const handleDelete = async (row) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'ลบเรทเหมาของพื้นที่นี้?',
      text: `${row.tambon ? `ต.${row.tambon_name} ` : ''}อ.${row.amper_name} จ.${row.province_name} — ลบทุกประเภทรถ`,
      showCancelButton: true,
      confirmButtonColor: '#d32f2f', cancelButtonColor: '#757575',
      confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก',
    });
    if (!r.isConfirmed) return;
    try {
      await api.post('/api/shipping-settings/areas/delete', {
        area: { province: row.province, amper: row.amper, tambon: row.tambon },
      }, { withCredentials: true });
      Swal.fire({ icon: 'success', title: 'ลบสำเร็จ', timer: 1200, showConfirmButton: false });
      load();
    } catch (err) { showError(err); }
  };

  const columns = [
    { field: 'province_name', headerName: 'จังหวัด', width: 130, sortable: false },
    {
      field: 'area', headerName: 'พื้นที่', flex: 1, minWidth: 220, sortable: false,
      valueGetter: (_, row) => `${row.amper_name} ${row.tambon_name || ''}`,
      renderCell: ({ row }) => (row.tambon ? (
        <Box sx={{ pl: 2.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color="text.disabled">↳</Typography>
          <Typography variant="body2">ต.{row.tambon_name || row.tambon}</Typography>
          <Typography variant="caption" color="text.secondary">(อ.{row.amper_name})</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>อ.{row.amper_name}</Typography>
          <Chip label="ทั้งอำเภอ" size="small" variant="outlined" color="primary" sx={{ height: 20, fontSize: 11 }} />
        </Box>
      )),
    },
    ...vehicles.map((v, i) => ({
      field: priceField(i),
      headerName: `${v.code} (บาท)`,
      width: 130,
      type: 'number',
      editable: true,
      headerAlign: 'right',
      renderCell: ({ value }) => (value === null || value === undefined
        ? <Typography variant="body2" color="text.disabled">—</Typography>
        : formatNumber(value)),
    })),
    {
      field: 'actions', headerName: '', width: 110, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {row.duplicates.length > 0 && (
            <Tooltip title={`มีเรทซ้ำ (${row.duplicates.join(', ')}) — แก้ราคาแล้วบันทึกเพื่อรวมเป็นแถวเดียว`}>
              <WarningAmberIcon fontSize="small" color="warning" />
            </Tooltip>
          )}
          <Tooltip title="แก้ไขพื้นที่/ราคา">
            <IconButton size="small" onClick={() => { setDialogTarget(row); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="ลบ">
            <IconButton size="small" color="error" onClick={() => handleDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 450 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>จังหวัด</InputLabel>
          <Select label="จังหวัด" value={provinceFilter} onChange={e => setProvinceFilter(e.target.value)}>
            <MenuItem value="">ทุกจังหวัด</MenuItem>
            {provinceOptions.map(([code, name]) => <MenuItem key={code} value={code}>{name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small" placeholder="ค้นหาอำเภอ / ตำบล" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="body2" color="text.secondary">{filtered.length} พื้นที่</Typography>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} disabled={!vehicles.length} onClick={() => { setDialogTarget(null); setDialogOpen(true); }}>
          เพิ่มพื้นที่
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary">
        ดับเบิลคลิกที่ช่องราคาเพื่อแก้ไข แล้วกด Enter เพื่อบันทึก (Esc = ยกเลิก) · ช่อง "—" = ไม่มีเรทเหมาสำหรับรถนั้น ระบบจะไปคิดตามระยะทางแทน
      </Typography>
      <DataGrid
        rows={filtered}
        columns={columns}
        loading={loading}
        density="compact"
        disableRowSelectionOnClick
        disableColumnMenu
        processRowUpdate={saveRowPrices}
        onProcessRowUpdateError={showError}
        getRowClassName={({ row }) => (row.tambon ? '' : 'amper-row')}
        sx={{
          flex: 1,
          '& .amper-row': { bgcolor: 'action.hover' },
          '& .MuiDataGrid-cell--editable': { cursor: 'text' },
        }}
      />
      <AreaDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        initial={dialogTarget}
        vehicles={vehicles}
        provinces={provinces}
        ampers={ampers}
        tambons={tambons}
        existingKeys={existingKeys}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={1500}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}

// ── แท็บตั้งค่าทั่วไป ─────────────────────────────────────────────────────────────

// คำนวณแบบเดียวกับขั้น "ตามระยะทาง" ใน backend (/calculateShipping) ด้วยค่าที่กำลังแก้อยู่
function estimateByDistance(settings, { distance, weight, length, amount }) {
  const d = Number(distance) || 0;
  const vehicle = [...settings.vehicles]
    .sort((a, b) => Number(a.max_weight) - Number(b.max_weight))
    .find(v => Number(v.max_weight) >= (Number(weight) || 0) && Number(v.max_length) >= (Number(length) || 0));
  if (!vehicle) return { error: 'ไม่มีรถที่รับน้ำหนัก/ความยาวนี้ได้' };
  const f = settings.free;
  if (d >= Number(f.min_distance) && d <= Number(f.max_distance) && (Number(amount) || 0) >= Number(f.min_sum_amount)) {
    return { vehicle: vehicle.code, cost: 0, note: 'ส่งฟรี (อยู่ในระยะและยอดถึงขั้นต่ำ)' };
  }
  const raw = Math.ceil((d * Number(vehicle.rate_per_km)) / 10) * 10;
  const min = Number(settings.min_cost) || 0;
  return raw < min
    ? { vehicle: vehicle.code, cost: min, note: `ใช้ค่าขั้นต่ำ (คำนวณได้ ${formatNumber(raw)} บาท)` }
    : { vehicle: vehicle.code, cost: raw, note: `${d} km × ${vehicle.rate_per_km} บาท ปัดขึ้นหลักสิบ` };
}

function SectionCard({ title, subtitle, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {children}
    </Paper>
  );
}

function NumberField({ label, value, onChange, unit, width }) {
  return (
    <TextField
      size="small" type="number" label={label} value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      inputProps={{ min: 0 }}
      error={value !== '' && value !== null && Number(value) < 0}
      sx={{ width: width || 'auto' }}
      InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
    />
  );
}

function GeneralSettingsTab({ saved, onSaved }) {
  const [settings, setSettings] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [example, setExample] = useState({ distance: 30, weight: 1000, length: 6, amount: 3000 });

  useEffect(() => { setSettings(saved); }, [saved]);

  const dirty = JSON.stringify(settings) !== JSON.stringify(saved);
  const setVehicle = (code, field, value) => setSettings(s => ({
    ...s, vehicles: s.vehicles.map(v => (v.code === code ? { ...v, [field]: value } : v)),
  }));
  const setFree = (field, value) => setSettings(s => ({ ...s, free: { ...s.free, [field]: value } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/api/shipping-settings/update', settings, { withCredentials: true });
      Swal.fire({ icon: 'success', title: 'บันทึกสำเร็จ', timer: 1200, showConfirmButton: false });
      onSaved();
    } catch (err) {
      showError(err);
    } finally { setSaving(false); }
  };

  const result = estimateByDistance(settings, example);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 9 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SectionCard title="ประเภทรถ" subtitle="ระบบเลือกรถคันเล็กสุดที่รับน้ำหนักและความยาวของสินค้าได้ แล้วใช้อัตราต่อ km ของรถคันนั้น">
            {settings.vehicles.map(v => (
              <Box key={v.code} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip icon={<LocalShippingIcon />} label={v.code} sx={{ minWidth: 90, fontWeight: 600 }} />
                <NumberField label="รับน้ำหนักสูงสุด" value={v.max_weight} onChange={val => setVehicle(v.code, 'max_weight', val)} unit="kg" width={170} />
                <NumberField label="ความยาวสูงสุด" value={v.max_length} onChange={val => setVehicle(v.code, 'max_length', val)} unit="m" width={150} />
                <NumberField label="อัตรา" value={v.rate_per_km} onChange={val => setVehicle(v.code, 'rate_per_km', val)} unit="บาท/km" width={150} />
              </Box>
            ))}
          </SectionCard>

          <SectionCard title="ค่าส่งขั้นต่ำ" subtitle="ถ้าคิดตามระยะทางแล้วได้น้อยกว่านี้ จะใช้ค่านี้แทน">
            <NumberField label="ค่าส่งขั้นต่ำ" value={settings.min_cost} onChange={val => setSettings(s => ({ ...s, min_cost: val }))} unit="บาท" width={200} />
          </SectionCard>

          <SectionCard title="ส่งฟรี" subtitle="ส่งฟรีเมื่อระยะทางจากคลังที่ใกล้สุดอยู่ในช่วงนี้ และยอดสินค้า (หลังหักส่วนลด) ถึงขั้นต่ำ">
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <NumberField label="ระยะทางตั้งแต่" value={settings.free.min_distance} onChange={val => setFree('min_distance', val)} unit="km" width={150} />
              <Typography color="text.secondary">ถึง</Typography>
              <NumberField label="ไม่เกิน" value={settings.free.max_distance} onChange={val => setFree('max_distance', val)} unit="km" width={150} />
              <NumberField label="ยอดสินค้าขั้นต่ำ" value={settings.free.min_sum_amount} onChange={val => setFree('min_sum_amount', val)} unit="บาท" width={190} />
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, position: { md: 'sticky' }, top: { md: 16 }, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" fontWeight={600}>ทดลองคำนวณ</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              ใช้ค่าที่กำลังแก้ (ยังไม่ต้องบันทึก) — เฉพาะกรณีคิดตามระยะทาง คือไม่มีบิลเก่าและไม่มีเรทเหมา
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <NumberField label="ระยะทาง" value={example.distance} onChange={val => setExample(e => ({ ...e, distance: val }))} unit="km" />
              <NumberField label="ยอดสินค้า" value={example.amount} onChange={val => setExample(e => ({ ...e, amount: val }))} unit="บาท" />
              <NumberField label="น้ำหนักรวม" value={example.weight} onChange={val => setExample(e => ({ ...e, weight: val }))} unit="kg" />
              <NumberField label="ยาวสุด" value={example.length} onChange={val => setExample(e => ({ ...e, length: val }))} unit="m" />
            </Box>
            <Divider sx={{ my: 2 }} />
            {result.error ? (
              <Alert severity="error">{result.error}</Alert>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary">รถ {result.vehicle}</Typography>
                <Typography variant="h4" fontWeight={700} color={result.cost === 0 ? 'success.main' : 'text.primary'}>
                  {result.cost === 0 ? 'ส่งฟรี' : `${formatNumber(result.cost)} บาท`}
                </Typography>
                <Typography variant="caption" color="text.secondary">{result.note}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* แถบบันทึกติดล่างจอ */}
      <Paper
        elevation={dirty ? 6 : 1}
        sx={{
          position: 'sticky', bottom: 0, zIndex: 2, px: 2, py: 1.25,
          display: 'flex', alignItems: 'center', gap: 1.5,
          borderTop: '3px solid', borderColor: dirty ? 'warning.main' : 'transparent',
        }}
      >
        <Typography variant="body2" color={dirty ? 'warning.dark' : 'text.secondary'} sx={{ flex: 1 }}>
          {dirty ? 'มีการแก้ไขที่ยังไม่บันทึก' : 'ไม่มีการเปลี่ยนแปลง'}
        </Typography>
        <Button onClick={() => setSettings(saved)} disabled={!dirty || saving} sx={{ color: '#757575' }}>คืนค่าเดิม</Button>
        <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? <CircularProgress size={18} /> : 'บันทึกการตั้งค่า'}
        </Button>
      </Paper>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ShippingSettings() {
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.post('/api/shipping-settings/get', {}, { withCredentials: true });
      setSettings(data);
    } catch (err) { showError(err); }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const vehicles = useMemo(() => settings?.vehicles || [], [settings]);

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" fontWeight={600}>ตั้งค่าค่าขนส่ง</Typography>
      <CalculationOrder />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Tab label="เรทเหมาตามพื้นที่" />
        <Tab label="อัตราตามระยะทาง / ส่งฟรี" />
      </Tabs>

      {!settings ? (
        <CircularProgress size={24} />
      ) : tab === 0 ? (
        <AreaRatesTab vehicles={vehicles} />
      ) : (
        <GeneralSettingsTab saved={settings} onSaved={loadSettings} />
      )}
    </Box>
  );
}
